import { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, Typography, TypographyProps } from '@mui/material';

type TypographyWithTooltipProps = TypographyProps & {
  title: string;
};

/** Typography that shows a tooltip when `noWrap` content is truncated. */
export const TypographyWithTooltip = (
  props: TypographyWithTooltipProps,
): JSX.Element => {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const { children, title, sx, ...rest } = props;

  const checkTruncation = useCallback((): void => {
    const el = ref.current;
    if (!el) {
      return;
    }

    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkTruncation();
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [checkTruncation, children, title]);

  return (
    <Tooltip title={isTruncated ? title : null}>
      <Typography
        {...rest}
        ref={ref}
        sx={{
          minWidth: 0,
          ...sx,
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};
